/**
 * LocalStorage Backend
 *
 * Persistent storage that survives page refreshes and browser restarts.
 * Falls back to memory storage if localStorage is unavailable or quota is exceeded.
 */

import { MemoryBackend } from './memory';
import type { StorageBackend, StorageBackendOptions } from './types';

export class LocalStorageBackend implements StorageBackend {
  private fallback: MemoryBackend | null = null;

  get(key: string): string | null {
    if (!this.isSupported()) {
      return this.getFallback().get(key);
    }

    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('localStorage.getItem failed:', error);
      return this.getFallback().get(key);
    }
  }

  set(key: string, value: string, _options?: StorageBackendOptions): void {
    if (!this.isSupported()) {
      this.getFallback().set(key, value);
      return;
    }

    try {
      localStorage.setItem(key, value);
    } catch (error) {
      // Quota exceeded or other error
      console.warn('localStorage.setItem failed, falling back to memory:', error);
      this.getFallback().set(key, value);
    }
  }

  remove(key: string): void {
    if (!this.isSupported()) {
      this.getFallback().remove(key);
      return;
    }

    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('localStorage.removeItem failed:', error);
      this.getFallback().remove(key);
    }
  }

  clear(): void {
    if (!this.isSupported()) {
      this.getFallback().clear();
      return;
    }

    try {
      localStorage.clear();
    } catch (error) {
      console.warn('localStorage.clear failed:', error);
      this.getFallback().clear();
    }
  }

  isSupported(): boolean {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private getFallback(): MemoryBackend {
    if (!this.fallback) {
      this.fallback = new MemoryBackend();
    }
    return this.fallback;
  }
}
